import { FullPageLoading } from "../UI/Loading/FullPageLoading.tsx";
import { Layout } from "../UI/Layout/Layout.tsx";
import { formatDateTime } from "../UI/Utils/formatDate.ts";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { useGetAllBulkClassRoomCreationRequests } from "./useGetAllBulkClassRoomCreationRequests.ts";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { BulkClassRoomCreationRequestStatus } from "../gql/graphql.ts";
import { useNavigate } from "react-router-dom";

export const BulkClassRoomCreationRequestsScreenPath =
  "/bulk-classrooms/requests";

export function BulkClassRoomCreationRequestsScreen() {
  const { requests, loading, error } = useGetAllBulkClassRoomCreationRequests();
  const navigate = useNavigate();

  if (loading) {
    return <FullPageLoading />;
  }

  if (error) {
    return (
      <Layout>
        <Card>
          <ErrorFeedback
            title={"Errore nel caricamento delle richieste"}
            message={error.message}
          />
        </Card>
      </Layout>
    );
  }

  const getStatusBadge = (status: BulkClassRoomCreationRequestStatus) => {
    if (status === BulkClassRoomCreationRequestStatus.Completed) {
      return (
        <span className="px-3 py-1 bg-green-600 text-white rounded-md text-sm font-medium">
          Completato
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-yellow-600 text-white rounded-md text-sm font-medium">
        In Creazione
      </span>
    );
  };

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock
          title={"Richieste Creazione Bulk ClassRoom"}
          breadCrumbs={
            <BreadCrumbsWrapper>
              <BreadCrumbsItem title={"Richieste Bulk ClassRoom"} />
            </BreadCrumbsWrapper>
          }
        />
      </Card>

      <div className={"mt-4"}>
        <Card>
          <h2 className="text-lg font-semibold mb-4">
            Richieste ({requests.length})
          </h2>
          {requests.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              Nessuna richiesta trovata
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="p-4 bg-gray-50 rounded-md border border-gray-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">ID: {request.id}</span>
                        {getStatusBadge(request.status)}
                      </div>
                      <div className="text-sm space-y-1">
                        <div>
                          <span className="font-medium">Insegnante:</span>{" "}
                          {request.teacher.givenName}{" "}
                          {request.teacher.familyName}
                        </div>
                        <div>
                          <span className="font-medium">Utente:</span>{" "}
                          {request.user.givenName} {request.user.familyName}
                        </div>
                        <div>
                          <span className="font-medium">Classi da creare:</span>{" "}
                          {request.totalClassRoomsToCreate}
                        </div>
                        <div>
                          <span className="font-medium">Creata il:</span>{" "}
                          {formatDateTime(request.createdAt, { locale: "it" })}
                        </div>
                        {request.updatedAt && (
                          <div>
                            <span className="font-medium">Aggiornata il:</span>{" "}
                            {formatDateTime(request.updatedAt, {
                              locale: "it",
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex gap-2">
                      <button
                        onClick={() =>
                          window.open(
                            `${import.meta.env.VITE_USER_APP_BASE_URL}/impersonate-user?uid=${request.user.authId}`,
                            "_blank",
                            "noopener noreferrer",
                          )
                        }
                        className="px-4 py-2 text-purple-600 hover:text-purple-900 font-medium border border-purple-600 hover:border-purple-900 rounded-md transition-colors"
                        title="Impersona utente"
                      >
                        Impersona
                      </button>
                      <button
                        onClick={() =>
                          navigate(`/bulk-classrooms/request/${request.id}`)
                        }
                        className="px-4 py-2 text-blue-600 hover:text-blue-900 font-medium border border-blue-600 hover:border-blue-900 rounded-md transition-colors"
                      >
                        Dettagli
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
